/**
 * PATH: packages/ui/src/kanban/components/KanbanCardPeek.tsx
 * FILE_ID: UI-KANBAN-CARD-PEEK-001
 * PURPOSE: Hover "thread peek" popover anchored to a card's right edge — the
 *   linked email conversation AI-summarised into action items, Trello-style.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-AI-THREAD — operator: "on the right if you
 *   hover you can see the conversation / action board sequence like in Trello
 *   threads … emails should be summarised into action structures too … this
 *   is where AI comes in". This is the FE half (Stage 2) of qb!3838's
 *   GET /api/kanban/cards/{id}/ai-thread/ endpoint.
 * FLOW:
 *   ┌────────────────┐   ┌───────────────────────────┐   ┌──────────────────┐
 *   │ open + anchor  │ → │ api.getCardAiThread(id)    │ → │ portal popover:  │
 *   │ rect from the  │   │ (cached server-side; pass  │   │ summary + action │
 *   │ peek handle    │   │ refresh:true to rebuild)   │   │ items + thread   │
 *   └────────────────┘   └───────────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - react-dom createPortal: escape the card's overflow/transform.
 *   - lucide-react: kind + chrome glyphs.
 *   - ../api KanbanApi.getCardAiThread, ../types CardAiThread.
 * RELATED:
 *   - ./KanbanCardPeekHandle.tsx: owns open/hover-intent state + mounts this.
 *   - email_kanban/views/card_actions/intelligence.py: the BE action.
 */
import { useEffect, useState, type Ref } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, MessageSquareText, RefreshCw, Sparkles, X } from 'lucide-react'

import type { KanbanApi, KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { useIsPhone } from '../useIsPhone'
import { Z_LAYER } from '../zLayers'
import { boardUnseenQueryKey } from './board-view/useBoardUnseenEmails'
import { KanbanCardPeekActions } from './KanbanCardPeekActions'
import { KanbanCardPeekComments } from './KanbanCardPeekComments'
import { KanbanCardPeekMeta } from './KanbanCardPeekMeta'
import { aiThreadQueryKey } from './peekQueryKeys'

const PANEL_W = 360
const PANEL_MAX_H = 480
const GAP = 8

export interface KanbanCardPeekProps {
  cardId: string
  cardTitle: string
  api: Pick<KanbanApi, 'getCardAiThread'> &
    Partial<
      Pick<
        KanbanApi,
        'listComments' | 'createComment' | 'deleteComment' | 'markEmailsSeen'
      >
    >
  /** Whether to fetch + render the AI email-thread half (summary + action items
   *  + conversation). When false the peek is comment-only — for manual cards
   *  that have no linked email. Defaults true (email-card / direct-caller
   *  behaviour is unchanged). */
  threadEnabled?: boolean
  /** Viewport rect of the trigger handle — the panel hugs its right edge,
   *  flipping to the left when there isn't room. */
  anchorRect: { top: number; right: number; left: number; bottom: number }
  onClose: () => void
  /** Keep-open / arm-close handlers so the panel is part of the hover zone. */
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  /** Ref to the rendered panel element. The handle hit-tests the pointer
   *  against it (hover keep-alive across the pill→panel gap) and uses it to
   *  ignore the panel's OWN scroll so reading the thread doesn't close it. */
  panelRef?: Ref<HTMLDivElement>
  /** Promote an AI action item to a real checklist step (FE stage 3). When
   *  omitted, action items render read-only. */
  onAddChecklistStep?: (text: string) => Promise<void>
  /** The full card — when supplied, the peek shows an identity/meta strip
   *  (assignee avatars = "whose card", priority, due, tags) under the header.
   *  Optional so direct callers / unit tests can mount with just an id. */
  card?: KanbanCardModel
  /** Resolve assignee + comment-author ids to members (avatars / names). */
  resolveMember?: (userId: number) => KanbanMember | undefined
  /** Scope comment cache invalidation to a board when known. */
  boardId?: string
  /** Current viewer id — scopes the LOCAL comment draft to this user (never
   *  visible to another user on a shared profile). Absent → device-scoped. */
  currentUserId?: string | number | null
}

function formatWhen(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Right-edge hover thread peek. Renders into a body portal so it escapes the
 *  card's overflow + hover-lift transform. All open/close timing lives in the
 *  parent handle; this is fetch + presentation only. */
export function KanbanCardPeek({
  cardId,
  cardTitle,
  api,
  threadEnabled = true,
  anchorRect,
  onClose,
  onMouseEnter,
  onMouseLeave,
  panelRef,
  onAddChecklistStep,
  card,
  resolveMember,
  boardId,
  currentUserId,
}: KanbanCardPeekProps) {
  const qc = useQueryClient()
  // Manual "rebuild the summary server-side" spinner — distinct from the
  // react-query background refetch the cache does on staleness.
  const [refreshing, setRefreshing] = useState(false)

  // ARC-5 U18 — opening the peek counts as SEEING the card's linked emails
  // (the popup renders the thread content). Fire-and-forget + idempotent;
  // the board's unseen aggregate is invalidated so chips/focus refresh.
  const seenEmailIds = card
    ? [card.primary_email, ...(card.related_emails ?? [])].filter(
        (id): id is string => !!id,
      )
    : []
  const seenIdsKey = seenEmailIds.join(',')
  useEffect(() => {
    if (!api.markEmailsSeen || seenEmailIds.length === 0) return
    api
      .markEmailsSeen(seenEmailIds, 'CARD_POPUP')
      .then(({ new: n }) => {
        if (n > 0) {
          void qc.invalidateQueries({ queryKey: boardUnseenQueryKey() })
        }
      })
      .catch(() => {
        /* best-effort — never block the peek on seen bookkeeping */
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id SET, not array identity
  }, [seenIdsKey])
  // On phones the peek is a bottom sheet (with a scrim), not an anchored
  // popover — touch has no hover zone and a 360px popover is unusable.
  const isPhone = useIsPhone()

  // Preloaded by the handle's hover prefetch into the SAME key, so by the time
  // this mounts the data is usually already in cache (instant, no skeleton).
  const aiQuery = useQuery({
    queryKey: aiThreadQueryKey(cardId),
    queryFn: () => api.getCardAiThread!(cardId),
    enabled: threadEnabled && !!api.getCardAiThread,
    // The summary is expensive to regenerate; trust the server-side cache.
    staleTime: 5 * 60_000,
    // Manual Retry button drives re-fetches; don't auto-retry a failed summary.
    retry: false,
  })

  async function refresh() {
    if (!api.getCardAiThread) return
    setRefreshing(true)
    try {
      const data = await api.getCardAiThread(cardId, { refresh: true })
      qc.setQueryData(aiThreadQueryKey(cardId), data)
    } finally {
      setRefreshing(false)
    }
  }

  // Position: right of the anchor (the card tile), flipped left when the
  // viewport is tight. BUG-2026-07-06-KANBAN-THREAD-PEEK-STUCK: BOTH axes are
  // clamped into the viewport now - the old math could push the panel off the
  // right edge (flip threshold met but the flipped slot clamped back over the
  // anchor) and let the fixed 480px height overflow a short window.
  const vw = window.innerWidth
  const vh = window.innerHeight
  const maxH = Math.min(PANEL_MAX_H, vh - 16)
  const placeLeft = vw - anchorRect.right < PANEL_W + GAP + 8
  const rawLeft = placeLeft
    ? anchorRect.left - PANEL_W - GAP
    : anchorRect.right + GAP
  const left = Math.min(Math.max(8, rawLeft), Math.max(8, vw - PANEL_W - 8))
  const top = Math.min(Math.max(8, anchorRect.top), Math.max(8, vh - maxH - 8))

  const data = aiQuery.data ?? null
  const loading = aiQuery.isLoading || refreshing
  const errorMessage =
    aiQuery.isError && !data
      ? aiQuery.error instanceof Error
        ? aiQuery.error.message
        : 'Could not load thread'
      : null

  // Comments need the full trio of card APIs; bundle them once (typed) so the
  // section renders only when the host actually wired card collaboration.
  const commentsApi =
    api.listComments && api.createComment && api.deleteComment
      ? {
          listComments: api.listComments,
          createComment: api.createComment,
          deleteComment: api.deleteComment,
          // Optional — hosts without it still get the feed; edit pencil hides
          // (FEAT-2026-07-06-KANBAN-COMMENT-LINKS).
          updateComment: (api as { updateComment?: KanbanApi['updateComment'] }).updateComment,
        }
      : null

  const content = (
    <>
      {/* Grab handle — phone bottom-sheet affordance only. */}
      {isPhone && (
        <div
          className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-neutral-300 dark:bg-zinc-600"
          aria-hidden
        />
      )}
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-neutral-100 px-3 py-2 dark:border-zinc-800">
        {threadEnabled ? (
          <Sparkles className="h-4 w-4 shrink-0 text-violet-500" aria-hidden />
        ) : (
          <MessageSquareText className="h-4 w-4 shrink-0 text-violet-500" aria-hidden />
        )}
        <span className="truncate text-[13px] font-semibold text-neutral-800 dark:text-zinc-100">
          {cardTitle}
        </span>
        <span className="ml-auto flex items-center gap-1">
          {data?.cached && (
            <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 dark:bg-zinc-800 dark:text-zinc-400">
              cached
            </span>
          )}
          {/* Rebuilding the AI summary only makes sense when there IS one —
              comment-only peeks (manual cards) hide it. */}
          {threadEnabled && (
            <button
              type="button"
              title="Refresh AI summary"
              aria-label="Refresh AI summary"
              onClick={() => void refresh()}
              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-zinc-800"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            type="button"
            title="Close"
            aria-label="Close thread peek"
            onClick={onClose}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-zinc-800"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>

      {/* Identity strip — WHO owns the card + priority / due / tags. Pinned
          under the header (outside the scroll area) so the answer to "whose
          card is this?" stays visible while the summary scrolls. */}
      {card && <KanbanCardPeekMeta card={card} resolveMember={resolveMember} />}

      <div className="flex-1 overflow-y-auto px-3 py-2.5">
        {loading && !data && (
          <div className="space-y-2 py-1" aria-live="polite">
            <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-100 dark:bg-zinc-800" />
            <div className="h-3 w-full animate-pulse rounded bg-neutral-100 dark:bg-zinc-800" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-neutral-100 dark:bg-zinc-800" />
            <p className="pt-1 text-[11px] text-neutral-400">Summarising the thread…</p>
          </div>
        )}

        {errorMessage && (
          <div className="py-2 text-[12px] text-rose-600 dark:text-rose-400">
            {errorMessage}
            <button
              type="button"
              onClick={() => void aiQuery.refetch()}
              className="ml-2 underline underline-offset-2 hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {data && !data.has_email && (
          <p className="py-3 text-center text-[12px] text-neutral-400 dark:text-zinc-500">
            No linked email to summarise.
          </p>
        )}

        {data && data.has_email && (
          <div className="space-y-3">
            {data.summary && (
              <p className="text-[12px] leading-relaxed text-neutral-700 dark:text-zinc-200">
                {data.summary}
              </p>
            )}

            {/* AI action items — each one-click promotable to a checklist step */}
            {data.action_items.length > 0 && (
              <KanbanCardPeekActions
                items={data.action_items}
                onAddChecklistStep={onAddChecklistStep}
                alwaysShowAdd={isPhone}
              />
            )}

            {data.action_items.length === 0 && data.summary && (
              <p className="text-[11px] italic text-neutral-400">No explicit next actions detected.</p>
            )}

            {/* Honest empty-state: the BE serves a deterministic glance even
                when the AI provider is down, so this is only reached for a
                genuinely contentless thread — never leave the "AI thread" peek
                looking blank/broken (BUG-2026-06-22-KANBAN-AI-SUMMARY-EMPTY-CACHED). */}
            {!data.summary && data.action_items.length === 0 && (
              <p className="text-[11px] italic text-neutral-400 dark:text-zinc-500">
                AI summary unavailable right now.
              </p>
            )}

            {/* Conversation thread */}
            {data.thread.length > 0 && (
              <div className="border-t border-neutral-100 pt-2 dark:border-zinc-800">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                  Conversation
                </p>
                <ol className="space-y-2">
                  {data.thread.map((email) => (
                    <li key={email.id} className="flex gap-2">
                      <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-300 dark:text-zinc-600" aria-hidden />
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-1.5">
                          <span className="truncate text-[11px] font-medium text-neutral-700 dark:text-zinc-200">
                            {email.from_name || email.from_address || 'Unknown'}
                          </span>
                          <span className="shrink-0 text-[10px] text-neutral-400">
                            {formatWhen(email.received_at)}
                          </span>
                        </div>
                        {email.snippet && (
                          <p className="truncate text-[11px] text-neutral-500 dark:text-zinc-400">
                            {email.snippet}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Human comments — collaboration in-place (operator: "I can't add
            comments underneath the threads"). Shown for ANY card the host
            wired comment APIs for, not just email-linked ones. */}
        {commentsApi && (
          <div className={data && data.has_email ? 'mt-3' : ''}>
            <KanbanCardPeekComments
              cardId={cardId}
              api={commentsApi}
              resolveMember={resolveMember}
              boardId={boardId}
              currentUserId={currentUserId}
            />
          </div>
        )}
      </div>
    </>
  )

  // Phone: full-width bottom sheet behind a tap-away scrim.
  if (isPhone) {
    return createPortal(
      <>
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-[1px]"
          style={{ zIndex: Z_LAYER.menuScrim }}
          onClick={onClose}
          aria-hidden
          data-testid="kanban-card-peek-scrim"
        />
        <div
          role="dialog"
          aria-label={`Thread for ${cardTitle}`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '80vh', zIndex: Z_LAYER.popover }}
          className="flex flex-col overflow-hidden rounded-t-2xl border-t border-neutral-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
        >
          {content}
        </div>
      </>,
      document.body,
    )
  }

  // Desktop: anchored popover hugging the trigger's right edge.
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Thread for ${cardTitle}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Portals bubble through the React tree — swallow clicks so interacting
      // with the peek never opens the card drawer or starts a drag/rename.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top, left, width: PANEL_W, maxHeight: maxH, zIndex: Z_LAYER.popover }}
      className="flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl ring-1 ring-black/5 dark:border-zinc-700 dark:bg-zinc-900"
    >
      {content}
    </div>,
    document.body,
  )
}
